/**
 * The follow-up as a journey, not a box of text.
 *
 * Chasing somebody is a sequence: no answer on Monday, a call-back agreed
 * on Wednesday, a demo of the upgrade on Friday, and then either a no or a
 * yes. Recorded as free text that is a paragraph nobody reads and nothing
 * anybody can filter; recorded as a step with an outcome it is a trail with
 * a shape, and "how many follow-ups died at no-response" becomes a question
 * with an answer.
 *
 * So every note carries one of five outcomes, and the two that end the
 * chase set up the close rather than performing it: somebody presses Close
 * having read what they just wrote, and the close records *how* it ended —
 * not interested, a plan upgradation, or a registration — which is what the
 * closed list is badged and filtered by afterwards.
 *
 * The client's own details sit above it, because the first thing a call
 * needs is who is being called.
 */
import { useState } from 'react';
import api from '../lib/api.js';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { Avatar, Pill, Spinner } from './Primitives.jsx';
import {
  stamp, workStamp, ago, TZ_LABEL, TZ_WORK_LABEL, followState, FOLLOW_META, STEP_META, CLOSED_META,
  REGISTERED, REG_META, regLabel, isPartner, pendingClose
} from '../domain/demos.js';

/* The order the day goes: tried and got nowhere, spoke and there is more to
   do, then the two that end it. Matches STEPS in routes/calendly.js, which is
   what the server validates a round against. */
const STEPS = [
  'no_response', 'couldnt_connect', 'mail_sent',
  'call_back', 'follow_up',
  'demo_rescheduled', 'not_interested', 'success'
];

/* The three that finish the chase. Kept as a set rather than repeated as an
   || chain, because every one of them has to be added in both places or the
   panel offers an ending it will not let anybody close. */
const ENDS = new Set(['demo_rescheduled', 'not_interested', 'success']);

/* The same three, read out, for the line that says how to finish. */
const ENDING_WORDS = [...ENDS].map(k => STEP_META[k][0]).join(', ')
  .replace(/, ([^,]*)$/, ' or $1');
const SUCCESS = [
  ['plan_upgrade', 'Plan upgradation'],
  ['registration', 'Registration']
];

/* ---------------------------------------------------------- who this is */

/**
 * The client, in one block.
 *
 * Read-only: the follow-up list is for chasing, and somebody about to make
 * a call wants the name, the number and what they came for — not a form.
 */
export function ClientCard({ b }) {
  const s = b.session || {};
  const who = b.client || b.contact;
  const [, rtone, ricon] = REG_META[REGISTERED(b)];

  return (
    <section className="fj-who">
      <header>
        <Avatar name={b.invitee.name || b.invitee.email || '?'} />
        <div>
          <b>{b.invitee.name || b.invitee.email || 'Unknown'}</b>
          <div className="fj-badges">
            {s.customerType ? (
              <Pill tone={s.customerType === 'B2B' ? 'gr-indigo' : 'gr-cyan'} className="dbk-client">
                {s.customerType === 'B2B' ? 'B2B partner' : 'B2C customer'}
              </Pill>
            ) : null}
            <Pill tone={rtone} icon={ricon || undefined} className="dbk-client">
              {regLabel(b)}
            </Pill>
            {s.demoCount > 1 ? (
              <Pill tone="gr-violet" className="dbk-client">Demo {s.demoIndex} of {s.demoCount}</Pill>
            ) : null}
            {isPartner(b) ? <Pill tone="gr-purple" className="dbk-client">Partner course</Pill> : null}
          </div>
        </div>
      </header>

      <dl>
        <div>
          <dt><Icon name="mail" />Email</dt>
          <dd>{b.invitee.email ? <a href={`mailto:${b.invitee.email}`}>{b.invitee.email}</a> : '—'}</dd>
        </div>
        <div>
          <dt><Icon name="phone" />Phone</dt>
          <dd>{b.invitee.phone ? <a href={`tel:${b.invitee.phone}`}>{b.invitee.phone}</a> : '—'}</dd>
        </div>
        <div>
          <dt><Icon name="building" />Business</dt>
          <dd>{s.business || '—'}</dd>
        </div>
        <div>
          <dt><Icon name="users" />Client ID</dt>
          <dd>{who && who.code ? <span className="dbk-code">{who.code}</span> : 'not on the books'}</dd>
        </div>
        <div>
          <dt><Icon name="cal" />Demo</dt>
          <dd title="Dubai time">{stamp(b.start)}</dd>
        </div>
        <div>
          <dt><Icon name="check" />Taken by</dt>
          <dd>{(s.attendees || []).length ? s.attendees.map(a => a.name).join(', ') : 'not recorded'}</dd>
        </div>
        {s.interest ? (
          <div className="wide">
            <dt><Icon name="search" />Asked about</dt>
            <dd className="fj-ask">{s.interest}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

/* -------------------------------------------------------------- the trail */

/** One note: what it came to, what was said, and when. */
function Step({
  n, at, by, text, kind, plan, editedAt, editedBy,
  first, onRemove, onEdit, busy
}) {
  const meta = kind ? STEP_META[kind] : null;

  /*
   * Correcting the round in the place it is read.
   *
   * The outcome as much as the note: picking Success by mistake ends the
   * chase and puts a Close button in front of somebody, and writing a second
   * round to contradict the first leaves both on the trail with neither
   * obviously right.
   */
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text || '');
  const [step, setStep] = useState(kind || '');
  const [asPlan, setAsPlan] = useState(plan || 'plan_upgrade');

  const begin = () => {
    setDraft(text || '');
    setStep(kind || '');
    setAsPlan(plan || 'plan_upgrade');
    setOpen(true);
  };

  if (open) {
    return (
      <li className="fj-editing">
        <span className="fj-no">{n}</span>
        <div className="fj-edit">
          <div className="fj-erow">
            <select value={step} onChange={e => setStep(e.target.value)} aria-label="Outcome">
              <option value="">No outcome</option>
              {STEPS.map(k => <option key={k} value={k}>{STEP_META[k][0]}</option>)}
            </select>
            {step === 'success' ? (
              <select value={asPlan} onChange={e => setAsPlan(e.target.value)} aria-label="Closed as">
                <option value="plan_upgrade">Plan upgradation</option>
                <option value="registration">Registration</option>
              </select>
            ) : null}
          </div>
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="What happened on this round?"
            onKeyDown={e => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && draft.trim()) {
                onEdit({ at, text: draft.trim(), kind: step, plan: asPlan });
                setOpen(false);
              }
            }}
          />
          <div className="fj-eacts">
            <button
              type="button"
              className="btn sm primary"
              disabled={!draft.trim() || busy}
              onClick={() => { onEdit({ at, text: draft.trim(), kind: step, plan: asPlan }); setOpen(false); }}
            >
              {busy ? <Spinner /> : <Icon name="check" />}Save
            </button>
            <button type="button" className="btn sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className={first ? 'handover' : ''}>
      <span className="fj-no">{first ? <Icon name="send" /> : n}</span>
      <div>
        <div className="fj-shead">
          {first ? (
            <Pill tone="gr-amber" icon="send" className="dbk-client">From the demo team</Pill>
          ) : meta ? (
            <Pill tone={meta[1]} icon={meta[2]} className="dbk-client">{meta[0]}</Pill>
          ) : null}
          {plan ? (
            <Pill tone={CLOSED_META[plan][1]} icon={CLOSED_META[plan][2]} className="dbk-client">
              {CLOSED_META[plan][0]}
            </Pill>
          ) : null}
          {/* The team's own clock: the demo above is Dubai's because that is
              when it is held, but this is when somebody here made the call. */}
          <small title={`${stamp(at)} — ${TZ_LABEL}`}>
            {by || 'system'} · {workStamp(at)} · {ago(at)}
          </small>
          {/* Off, not edited. A round is what somebody said on a call; the
              honest correction to the wrong one is to take it off and write
              the right one, not to rewrite what is already read. The demo
              team's hand-over has no remove — it is not ours to take back. */}
          {/* Said, not hidden: a line other people have already read should
              not change underneath them without a mark. */}
          {editedAt ? (
            <small className="fj-edited" title={`Edited ${workStamp(editedAt)}`}>
              edited{editedBy ? ` by ${editedBy}` : ''}
            </small>
          ) : null}
          {onEdit && !first ? (
            <button
              type="button"
              className="fj-drop edit"
              title="Correct this round"
              disabled={busy}
              onClick={begin}
            >
              <Icon name="edit" />
            </button>
          ) : null}
          {onRemove && !first ? (
            <button
              type="button"
              className="fj-drop"
              title="Take this round back off"
              disabled={busy}
              onClick={onRemove}
            >
              {busy ? <Spinner /> : <Icon name="trash" />}
            </button>
          ) : null}
        </div>
        <p>{text}</p>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------- the whole */

export function FollowJourney({ b, mayEdit, reload, toast, onSettled, readOnly = false }) {
  const s = b.session;
  const fs = followState(b);
  const notes = s.feedback || [];
  const { patchDemo } = useApp();

  /**
   * What every button that moves the row between lists does afterwards.
   *
   * The server answers with the session it wrote, so that goes in at once -
   * reading the bookings again is a minute of Calendly requests, and a row
   * that stays where it was for a minute reads as a button that did
   * nothing. Then the panel folds: closing or reopening moves the row to a
   * different list, and leaving it open leaves it open on a row that is no
   * longer the one underneath.
   */
  /*
   * `leaves` is whether the row is about to go somewhere else.
   *
   * Starting, closing, reopening and unstarting all move the booking between
   * the lists the page is filtered by, so the panel folds and the eye goes
   * back to where the work is. Writing, correcting or removing a round does
   * not: the row stays exactly where it was, and folding it threw somebody
   * back to the top of a list of eighty to find the line they had just
   * edited — and it was no longer open to be found.
   */
  const settle = async (session, note, leaves = true) => {
    if (session) patchDemo(session.id, session);
    toast(note);
    if (leaves && onSettled) onSettled();
    await reload();
  };

  const [kind, setKind] = useState('follow_up');
  const [plan, setPlan] = useState('plan_upgrade');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');

  const live = fs === 'following';
  const closed = fs === 'closed';
  const ends = ENDS.has(kind);
  /* Read off the trail, not remembered: see pendingClose(). */
  const ending = pendingClose(b);

  const add = async () => {
    const t = text.trim();
    if (!t) { toast('Write the note first'); return; }
    setBusy('add');
    try {
      const r = await api.calendly.feedback(s.id, {
        text: t, kind, ...(kind === 'success' ? { plan } : {})
      });
      setText('');
      /* The panel stays open here: a round is not the end of anything, and
         several often go in one after another. */
      if (r.session) patchDemo(r.session.id, r.session);
      toast(r.closes
        ? `${STEP_META[kind][0]} recorded — press Close to finish`
        : `${STEP_META[kind][0]} recorded`);
      await reload();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  /* Only ever called with an ending in hand - the button does not exist
     until a round has been written that ends the chase, so nothing here
     has to invent a reason nobody typed. */
  const close = async () => {
    if (!ending) return;
    setBusy('close');
    try {
      const r = await api.calendly.update(s.id, {
        followStatus: 'closed', closedAs: ending.as, closedReason: ending.why.slice(0, 200)
      });
      await settle(r && r.session, `Closed — ${CLOSED_META[ending.as][0]}`);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const start = async () => {
    setBusy('start');
    try {
      const r = await api.calendly.update(s.id, { followStatus: 'following' });
      await settle(r && r.session, 'Following up');
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  /*
   * Pressed it by mistake.
   *
   * "Start follow-up" is a primary button on every handed-over row, and the
   * rows are a list — so it gets caught on the way past, and there was no way
   * back. The row then sits in Following up with nobody chasing it, which is
   * worse than either of the two honest states.
   *
   * Only while nothing has been logged. Once somebody has written a round the
   * chase really did start, and unwinding it would leave those rounds hanging
   * off a follow-up that never began.
   */
  /*
   * Take a round back off.
   *
   * Typed into a box under a button that saves on Enter, so the wrong one
   * gets written — and the only way out was to leave it and explain it in the
   * next round. Asked for first, because it is a line somebody else may
   * already have read.
   */
  const dropRound = async at => {
    if (!window.confirm('Take this round off the follow-up? The note goes with it.')) return;
    setBusy(`drop:${at}`);
    try {
      const r = await api.calendly.removeRound(s.id, at);
      await settle(r && r.session, 'Round removed', false);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  /* Correct one that was written wrong — the outcome as much as the note. */
  const fixRound = async body => {
    setBusy(`drop:${body.at}`);
    try {
      const r = await api.calendly.editRound(s.id, body);
      await settle(r && r.session, 'Round corrected', false);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const unstart = async () => {
    setBusy('unstart');
    try {
      const r = await api.calendly.update(s.id, { followStatus: 'handed' });
      await settle(r && r.session, 'Back to not started');
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const reopen = async () => {
    setBusy('reopen');
    try {
      const r = await api.calendly.update(s.id, { followStatus: s.followPrev || 'following' });
      await settle(r && r.session, 'Reopened — following up again');
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const [flabel, ftone, ficon] = FOLLOW_META[fs] || FOLLOW_META.handed;
  const end = closed && s.closedAs ? CLOSED_META[s.closedAs] || CLOSED_META.other : null;

  return (
    <section className="fj">
      <header className="fj-head">
        <h5>Follow-up</h5>
        <Pill tone={ftone} icon={ficon} className="dbk-client">{flabel}</Pill>
        {end ? <Pill tone={end[1]} icon={end[2]} className="dbk-client">{end[0]}</Pill> : null}
        {/* The rounds below are stamped on the clock they were worked on,
            which is not the clock the demo was held on. Saying which is the
            whole reason a reader can trust either. */}
        <span className="fj-tz">Rounds in {TZ_WORK_LABEL}</span>
      </header>

      {notes.length ? (
        <ol className="fj-trail">
          {notes.map((f, i) => (
            <Step
              key={i}
              n={i}
              first={i === 0 && !f.kind}
              {...f}
              onRemove={mayEdit && !readOnly && !closed ? () => dropRound(f.at) : undefined}
              onEdit={mayEdit && !readOnly && !closed ? fixRound : undefined}
              busy={busy === `drop:${f.at}`}
            />
          ))}
        </ol>
      ) : (
        <p className="dbk-hint">Nothing written yet.</p>
      )}

      {closed ? (
        <div className="fj-closed">
          <p>
            <b>Closed{s.closedAsLabel ? ` — ${s.closedAsLabel}` : ''}.</b>
            {s.closedReason ? ` “${s.closedReason}”` : ''}
          </p>
          {mayEdit && !readOnly ? (
            <button type="button" className="btn sm" disabled={!!busy} onClick={reopen}>
              {busy === 'reopen' ? <Spinner /> : <Icon name="undo" />}Reopen
            </button>
          ) : null}
        </div>
      ) : null}

      {/* The chase is only recorded once somebody has taken it on: until
          then the row is a hand-over waiting in the queue, and a note on it
          would be sales writing before sales has it. */}
      {!live || readOnly || !mayEdit ? null : (
        <div className="fj-add">
          {/* Once an ending round is on the trail the form is shut: the
              follow-up has one thing left to do, and offering another
              outcome beside the Close button asks which of two answers the
              row is giving. */}
          <div className="fj-row">
            <label>
              <span>Outcome</span>
              <select value={ending ? ending.as === 'not_interested' ? 'not_interested' : 'success' : kind}
                disabled={!!ending}
                onChange={e => setKind(e.target.value)}
              >
                {STEPS.map(k => <option key={k} value={k}>{STEP_META[k][0]}</option>)}
              </select>
            </label>

            {!ending && kind === 'success' ? (
              <label>
                <span>Closed as</span>
                <select value={plan} onChange={e => setPlan(e.target.value)}>
                  {SUCCESS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
            ) : null}

            <label className="grow">
              <span>Note</span>
              <input
                value={text}
                disabled={!!ending}
                placeholder={ending ? 'The follow-up has ended — press Close to finish it' : 'What happened on this round?'}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              />
            </label>

            <button type="button" className="btn sm primary" disabled={!!ending || !text.trim() || !!busy} onClick={add}>
              {busy === 'add' ? <Spinner /> : <Icon name="plus" />}Add
            </button>
          </div>

          {ending ? (
            <p className="fj-note stop">
              <Icon name="alert" />
              This follow-up ended with <b>{CLOSED_META[ending.as][0]}</b>. Press
              Close to finish it — no other outcome can be recorded until you do.
            </p>
          ) : ends ? (
            <p className="fj-note">
              <Icon name="alert" />
              This ends the follow-up. Add the note, then press Close.
            </p>
          ) : live ? (
            /*
             * How to finish it, said before somebody goes looking.
             *
             * There is no Close button until a round has been written that
             * ends the chase — deliberately, because a follow-up closed with
             * nothing said about why is a row nobody can learn anything from.
             * But the rule was invisible: the only hint appeared after you
             * had already picked an ending, which is no use to somebody
             * wondering how to get out of the screen.
             */
            <p className="fj-note quiet">
              <Icon name="flag" />
              To finish this follow-up, pick {ENDING_WORDS} as the outcome and
              write what happened — the Close button appears once it is in.
            </p>
          ) : null}

          {/* The only way to close is to have written the round that ends
              it. No button until then: a follow-up closed with nothing
              said about why is a row nobody can learn anything from. */}
          {ending ? (
            <div className="fj-acts">
              <button type="button" className="btn sm danger" disabled={!!busy} onClick={close}>
                {busy === 'close' ? <Spinner /> : <Icon name="check" />}
                Close — {CLOSED_META[ending.as][0]}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {fs === 'handed' && mayEdit && !readOnly ? (
        <div className="fj-acts">
          <button type="button" className="btn sm primary" disabled={!!busy} onClick={start}>
            {busy === 'start' ? <Spinner /> : <Icon name="flag" />}Start follow-up
          </button>
          <span className="dim">Take it on, and the rounds of chasing are recorded here.</span>
        </div>
      ) : null}

      {/* The way back from a mis-click. Offered only while the chase is
          genuinely empty — see unstart(). */}
      {fs === 'following' && !notes.length && mayEdit && !readOnly ? (
        <div className="fj-acts">
          <button type="button" className="btn sm" disabled={!!busy} onClick={unstart}>
            {busy === 'unstart' ? <Spinner /> : <Icon name="undo" />}Not started after all
          </button>
          <span className="dim">Nothing has been logged yet, so this can go back.</span>
        </div>
      ) : null}
    </section>
  );
}
